import React, {FC} from 'react';

import bgm1 from 'assets/v3/guide/video/bgm-1.webp';
import bgm2 from 'assets/v3/guide/video/bgm-2.webp';
import dubConfig from 'assets/v3/guide/video/dub-config.webp';
import editEntry from 'assets/v3/guide/video/edit-entry.webp';
import editSubtitle from 'assets/v3/guide/video/edit-subtitle.webp';
import exportImg from 'assets/v3/guide/video/export.webp';
import intro from 'assets/v3/guide/video/intro.webp';
import subtitleStyle1 from 'assets/v3/guide/video/subtitle-style-1.webp';
import subtitleStyle2 from 'assets/v3/guide/video/subtitle-style-2.webp';
import translating from 'assets/v3/guide/video/translating.webp';
import ttsConfig from 'assets/v3/guide/video/tts-config.webp';
import upload from 'assets/v3/guide/video/upload.webp';
import voiceToggle1 from 'assets/v3/guide/video/voice-toggle-1.webp';
import voiceToggle2 from 'assets/v3/guide/video/voice-toggle-2.webp';

/** 使用指南「视频翻译」tab 英文正文（与 zh.tsx 元素结构 1:1） */
const EnContent: FC = () => {
  return (
    <>
      <h2>Video Translation Overview</h2>
      <ul>
        <li>
          <strong>Audio recognition translation</strong>: supports uploading videos with pure human voice and no
          subtitles (videos with original subtitles are also supported) — the audio is recognized and translated into a
          foreign language, and you can choose whether to add subtitles as needed.
        </li>
        <li>
          <strong>Subtitle recognition translation</strong>: supports uploading videos with original subtitles. AI
          translates the subtitles automatically, then dubs the translated subtitles with a foreign-language voice.
        </li>
        <li>
          <strong>Text removal</strong>: upload videos containing on-screen text and the text will be recognized and
          removed.
        </li>
      </ul>
      <img alt="Video translation overview" decoding="async" loading="lazy" src={intro} />

      <h2>Step 1: Upload a Video</h2>
      <p>
        Here we take voice recognition translation as an example: select the languages you need and upload the video.
      </p>
      <img alt="Upload a video and select languages" decoding="async" loading="lazy" src={upload} />

      <h3>AI Dubbing Settings</h3>
      <p>
        AI dubbing is enabled by default. Click the panel on the right of the dubbing option to preview voices in a
        pop-up and pick the one you like.
      </p>
      <img alt="AI dubbing settings" decoding="async" loading="lazy" src={dubConfig} />

      <h3>Background Music</h3>
      <p>
        Enabling background music removes the vocals from the original audio by default — it strips the narration while
        keeping the music and sound effects. You can also pick a track from our music library.
      </p>
      <img alt="Background music settings" decoding="async" loading="lazy" src={bgm1} />
      <img alt="Music library" decoding="async" loading="lazy" src={bgm2} />

      <h3>Subtitle Style Settings</h3>
      <p>Turn subtitles on or off as needed. When enabled, the subtitle style and position are fully customizable.</p>
      <img alt="Subtitle style settings" decoding="async" loading="lazy" src={subtitleStyle1} />
      <img alt="Subtitle position settings" decoding="async" loading="lazy" src={subtitleStyle2} />

      <p>Once the upload is complete, the video starts translating.</p>
      <img alt="Video translation in progress" decoding="async" loading="lazy" src={translating} />

      <h2>Step 2: Edit Subtitles and Dubbing</h2>
      <p>
        After translation, you can preview and download the video directly, or edit it further. Click the 【Edit】button
        to open the video translation workbench.
      </p>
      <img alt="Open the video editing workbench" decoding="async" loading="lazy" src={editEntry} />

      <p>
        Click any subtitle or text and an 【Edit Subtitle】 pop-up appears on the right. In the text editing area you
        can modify the content, font, size, color and more of the selected subtitle.
      </p>
      <img alt="Edit subtitle pop-up" decoding="async" loading="lazy" src={editSubtitle} />

      <h3>Dubbing Settings</h3>
      <p>
        Click 【Dubbing Settings】 to adjust the voice-over of subtitles. After modifying the subtitle content or
        dubbing settings, click 【Preview】 to regenerate the updated voice-over audio and replace the automatic
        dubbing.
      </p>
      <img alt="Dubbing settings" decoding="async" loading="lazy" src={ttsConfig} />

      <p>
        If a title should not be read aloud, or a line is skipped, click that subtitle to toggle between voiced subtitle
        and plain text (you can switch back and forth freely).
      </p>
      <img alt="Toggle between voiced subtitle and plain text" decoding="async" loading="lazy" src={voiceToggle1} />
      <img alt="Subtitle voice toggle" decoding="async" loading="lazy" src={voiceToggle2} />

      <h2>Step 3: Export the Video</h2>
      <p>
        When everything looks good, click the 【Export】button in the top-right corner, choose the quality you need and
        export the video for publishing.
      </p>
      <img alt="Export the video" decoding="async" loading="lazy" src={exportImg} />
    </>
  );
};

export default EnContent;
